import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PlusIcon,
  UserIcon,
  BuildingOfficeIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  ShoppingBagIcon,
  CurrencyDollarIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ChartBarIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  KeyIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import AddCustomer from '../../components/admin/AddCustomer';
import CustomerDetails from '../../components/admin/CustomerDetails';
import EditCustomer from '../../components/admin/EditCustomer';
import ChangePassword from '../../components/admin/ChangePassword';

const AdminCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [showCustomerDetailsModal, setShowCustomerDetailsModal] = useState(false);
  const [showEditCustomerModal, setShowEditCustomerModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  
  // Filters and search
  const [filters, setFilters] = useState({
    search: '',
    type: '', // 'b2c', 'b2b'
    status: '', // 'active', 'inactive'
    company: '',
    sortBy: 'created_at',
    sortOrder: 'desc'
  });

  // Pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  });

  // Stats
  const [stats, setStats] = useState({
    totalCustomers: 0,
    b2cCustomers: 0,
    b2bCustomers: 0,
    activeCustomers: 0,
    newThisMonth: 0,
    totalRevenue: 0,
    averageOrderValue: 0
  });

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    // Load filters from URL
    const urlFilters = {
      search: searchParams.get('search') || '',
      type: searchParams.get('type') || '',
      status: searchParams.get('status') || '',
      company: searchParams.get('company') || '',
      sortBy: searchParams.get('sort') || 'created_at',
      sortOrder: searchParams.get('order') || 'desc'
    };
    setFilters(urlFilters);
    
    fetchCustomers();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchCustomers = async (customFilters = null, page = null) => {
    try {
      setLoading(true);
      const activeFilters = customFilters || filters;
      const currentPage = page || pagination.currentPage;
      
      // Build query params
      const params = new URLSearchParams();
      if (activeFilters.search) params.append('search', activeFilters.search);
      if (activeFilters.type) params.append('role', activeFilters.type);
      if (activeFilters.status) params.append('status', activeFilters.status);
      if (activeFilters.company) params.append('company', activeFilters.company);
      params.append('sort', activeFilters.sortBy);
      params.append('order', activeFilters.sortOrder);
      params.append('page', currentPage);
      params.append('limit', pagination.itemsPerPage);

      const response = await axios.get(`/api/users?${params.toString()}`);
      // Note: /api/users endpoint returns all users including customers, filtered by admin auth

      setCustomers(response.data.users || []);  // Backend returns 'users' not 'customers'
      setPagination(response.data.pagination || pagination);

    } catch (error) {
      console.error('Error fetching customers:', error);
      // Show empty state instead of mock data
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/admin/customers/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      // Set empty stats on error instead of mock data
      setStats({
        totalCustomers: 0,
        b2cCustomers: 0,
        b2bCustomers: 0,
        activeCustomers: 0,
        newThisMonth: 0,
        totalRevenue: 0,
        averageOrderValue: 0
      });
    }
  };


  const handleSearch = (e) => {
    e.preventDefault();
    updateFilters({ ...filters, search: e.target.search.value });
  };

  const updateFilters = (newFilters) => {
    setFilters(newFilters);
    
    // Reset to page 1 when filters change
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
    
    // Update URL
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([key, value]) => {
      if (value && value !== '') {
        if (key === 'sortBy') {
          params.append('sort', value);
        } else if (key === 'sortOrder') {
          params.append('order', value);
        } else {
          params.append(key, value);
        }
      }
    });
    
    setSearchParams(params);
    
    // Fetch customers with new filters from page 1
    fetchCustomers(newFilters, 1);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    
    setPagination(prev => ({
      ...prev,
      currentPage: newPage
    }));
    
    // Fetch customers for the new page
    fetchCustomers(filters, newPage);
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return '₪0.00';
    }
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) {
      return 'אין הזמנות';
    }
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'תאריך לא תקין';
      }
      return date.toLocaleDateString('he-IL', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'תאריך לא תקין';
    }
  };

  const handleCustomerAdded = (newCustomer) => {
    // Add the new customer to the list
    setCustomers(prevCustomers => [newCustomer, ...prevCustomers]);
    
    // Update stats
    setStats(prev => ({
      ...prev,
      totalCustomers: prev.totalCustomers + 1,
      [newCustomer.role === 'b2b' ? 'b2bCustomers' : 'b2cCustomers']: 
        prev[newCustomer.role === 'b2b' ? 'b2bCustomers' : 'b2cCustomers'] + 1,
      activeCustomers: newCustomer.status === 'active' ? prev.activeCustomers + 1 : prev.activeCustomers,
      newThisMonth: prev.newThisMonth + 1
    }));
    
    // Update pagination if needed
    setPagination(prev => ({
      ...prev,
      totalItems: prev.totalItems + 1
    }));
    
    // Refresh the customers list to get updated data from server
    fetchCustomers();
  };

  const handleViewCustomer = (customer) => {
    setSelectedCustomer(customer);
    setShowCustomerDetailsModal(true);
  };

  const handleEditCustomer = (customer) => {
    setSelectedCustomer(customer);
    setShowEditCustomerModal(true);
  };

  const handleChangePassword = (customer) => {
    setSelectedCustomer(customer);
    setShowChangePasswordModal(true);
  };

  const handleCustomerUpdated = (updatedCustomer) => {
    // Update the customer in the list
    setCustomers(prevCustomers =>
      prevCustomers.map(customer =>
        customer.id === updatedCustomer.id ? { ...customer, ...updatedCustomer } : customer
      )
    );
    
    // Close modals and clear selected customer
    setShowEditCustomerModal(false);
    setSelectedCustomer(null);
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-red-100 text-red-800'
    };
    
    const labels = {
      active: 'פעיל',
      inactive: 'לא פעיל'
    };

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badges[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const StatCard = ({ title, value, change, icon: Icon, color, href }) => (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
          {change !== undefined && (
            <div className="flex items-center mt-2">
              {change >= 0 ? (
                <ArrowUpIcon className="h-4 w-4 text-green-500 ml-1" />
              ) : (
                <ArrowDownIcon className="h-4 w-4 text-red-500 ml-1" />
              )}
              <span className={`text-sm font-medium ${
                change >= 0 ? 'text-green-500' : 'text-red-500'
              }`}>
                {Math.abs(change)}%
              </span>
              <span className="text-sm text-gray-500 mr-2">מהחודש הקודם</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          <Icon className="h-8 w-8 text-white" />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">ניהול לקוחות</h1>
          <p className="text-gray-600 mt-2">ניהול בסיס הלקוחות והקשרים העסקיים</p>
        </div>
        <div className="flex space-x-3 mt-4 sm:mt-0">
          <button className="btn btn-outline flex items-center">
            <ChartBarIcon className="h-4 w-4 ml-2" />
            דוח לקוחות
          </button>
          <button 
            onClick={() => setShowAddCustomerModal(true)}
            className="btn btn-primary flex items-center"
          >
            <PlusIcon className="h-4 w-4 ml-2" />
            הוסף לקוח
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="סך לקוחות"
          value={stats.totalCustomers.toLocaleString('he-IL')}
          change={8.5}
          icon={UserIcon}
          color="bg-blue-500"
        />
        <StatCard
          title="לקוחות B2C"
          value={stats.b2cCustomers.toLocaleString('he-IL')}
          change={12.3}
          icon={UserIcon}
          color="bg-green-500"
        />
        <StatCard
          title="לקוחות B2B"
          value={stats.b2bCustomers.toLocaleString('he-IL')}
          change={-2.1}
          icon={BuildingOfficeIcon}
          color="bg-purple-500"
        />
        <StatCard
          title="הכנסות כוללות"
          value={formatCurrency(stats.totalRevenue)}
          change={15.7}
          icon={CurrencyDollarIcon}
          color="bg-orange-500"
        />
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <form onSubmit={handleSearch} className="flex gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              חיפוש לקוחות
            </label>
            <div className="relative">
              <input
                type="text"
                name="search"
                placeholder="חפש לפי שם, אימייל או חברה..."
                className="input pl-10"
                defaultValue={filters.search}
              />
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            </div>
          </div>
          
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`btn ${showFilters ? 'btn-primary' : 'btn-outline'} flex items-center`}
          >
            <FunnelIcon className="h-4 w-4 ml-2" />
            מסננים
          </button>
          
          <button type="submit" className="btn btn-primary">
            חפש
          </button>
        </form>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  סוג לקוח
                </label>
                <select
                  className="input"
                  value={filters.type}
                  onChange={(e) => updateFilters({ ...filters, type: e.target.value })}
                >
                  <option value="">כל הסוגים</option>
                  <option value="b2c">B2C - לקוח פרטי</option>
                  <option value="b2b">B2B - לקוח עסקי</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  סטטוס
                </label>
                <select
                  className="input"
                  value={filters.status}
                  onChange={(e) => updateFilters({ ...filters, status: e.target.value })}
                >
                  <option value="">כל הסטטוסים</option>
                  <option value="active">פעיל</option>
                  <option value="inactive">לא פעיל</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  מיון
                </label>
                <select
                  className="input"
                  value={`${filters.sortBy}-${filters.sortOrder}`}
                  onChange={(e) => {
                    const [sortBy, sortOrder] = e.target.value.split('-');
                    updateFilters({ ...filters, sortBy, sortOrder });
                  }}
                >
                  <option value="created_at-desc">תאריך הצטרפות (חדש לישן)</option>
                  <option value="created_at-asc">תאריך הצטרפות (ישן לחדש)</option>
                  <option value="total_spent-desc">סכום הוצאה (גבוה לנמוך)</option>
                  <option value="total_spent-asc">סכום הוצאה (נמוך לגבוה)</option>
                  <option value="total_orders-desc">מספר הזמנות (הרבה למעט)</option>
                  <option value="last_name-asc">שם משפחה (א-ת)</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Customers Table */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  לקוח
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  סוג
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  יצירת קשר
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  סטטיסטיקות
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  הזמנה אחרונה
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  סטטוס
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  פעולות
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {customers.map((customer) => (
                <tr key={customer.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-12 w-12">
                        <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center">
                          <span className="text-white font-semibold text-lg">
                            {customer.first_name.charAt(0)}
                          </span>
                        </div>
                      </div>
                      <div className="mr-4">
                        <div className="text-sm font-medium text-gray-900">
                          {customer.first_name} {customer.last_name}
                        </div>
                        {customer.company_name && (
                          <div className="text-sm text-blue-600">
                            {customer.company_name}
                          </div>
                        )}
                        <div className="text-sm text-gray-500 flex items-center">
                          <MapPinIcon className="h-4 w-4 ml-1" />
                          {customer.city}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {customer.role === 'b2b' ? (
                        <BuildingOfficeIcon className="h-5 w-5 text-purple-500 ml-2" />
                      ) : (
                        <UserIcon className="h-5 w-5 text-green-500 ml-2" />
                      )}
                      <span className="text-sm text-gray-900">
                        {customer.role === 'b2b' ? 'B2B' : 'B2C'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1">
                      <div className="flex items-center text-sm text-gray-900">
                        <EnvelopeIcon className="h-4 w-4 text-gray-400 ml-2" />
                        <span className="truncate max-w-32">{customer.email}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <PhoneIcon className="h-4 w-4 text-gray-400 ml-2" />
                        <span>{customer.phone}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1">
                      <div className="flex items-center text-sm text-gray-900">
                        <ShoppingBagIcon className="h-4 w-4 text-gray-400 ml-2" />
                        <span>{customer.total_orders || 0} הזמנות</span>
                      </div>
                      <div className="flex items-center text-sm text-green-600 font-medium">
                        <CurrencyDollarIcon className="h-4 w-4 ml-1" />
                        <span>{formatCurrency(customer.total_spent)}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(customer.last_order_date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(customer.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleViewCustomer(customer)}
                        className="text-primary-600 hover:text-primary-900"
                        title="צפה בלקוח"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEditCustomer(customer)}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="ערוך לקוח"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleChangePassword(customer)}
                        className="text-yellow-600 hover:text-yellow-900"
                        title="שנה סיסמה"
                      >
                        <KeyIcon className="h-4 w-4" />
                      </button>
                      <button
                        className="text-green-600 hover:text-green-900"
                        title="שלח אימייל"
                      >
                        <EnvelopeIcon className="h-4 w-4" />
                      </button>
                      <button
                        className="text-red-600 hover:text-red-900"
                        title="מחק לקוח"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              הקודם
            </button>
            <button
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.totalPages}
              className="mr-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              הבא
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                מציג{' '}
                <span className="font-medium">
                  {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1}
                </span>{' '}
                עד{' '}
                <span className="font-medium">
                  {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)}
                </span>{' '}
                מתוך{' '}
                <span className="font-medium">{pagination.totalItems}</span>{' '}
                לקוחות
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  הקודם
                </button>
                
                {/* Page Numbers */}
                <div className="flex items-center">
                  {[...Array(Math.min(pagination.totalPages, 5))].map((_, i) => {
                    let pageNum;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else {
                      const start = Math.max(1, pagination.currentPage - 2);
                      const end = Math.min(pagination.totalPages, start + 4);
                      pageNum = start + i;
                      if (pageNum > end) return null;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          pageNum === pagination.currentPage
                            ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  הבא
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Empty State */}
      {customers.length === 0 && !loading && (
        <div className="text-center py-12">
          <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">אין לקוחות</h3>
          <p className="mt-1 text-sm text-gray-500">התחל על ידי הוספת לקוח ראשון</p>
          <div className="mt-6">
            <button 
              onClick={() => setShowAddCustomerModal(true)}
              className="btn btn-primary"
            >
              <PlusIcon className="h-4 w-4 ml-2" />
              הוסף לקוח ראשון
            </button>
          </div>
        </div>
      )}

      {/* Add Customer Modal */}
      <AddCustomer
        isOpen={showAddCustomerModal}
        onClose={() => setShowAddCustomerModal(false)}
        onCustomerAdded={handleCustomerAdded}
      />

      {/* Customer Details Modal */}
      <CustomerDetails
        isOpen={showCustomerDetailsModal}
        onClose={() => {
          setShowCustomerDetailsModal(false);
          setSelectedCustomer(null);
        }}
        customer={selectedCustomer}
        onCustomerUpdated={handleCustomerUpdated}
      />

      {/* Edit Customer Modal */}
      <EditCustomer
        isOpen={showEditCustomerModal}
        onClose={() => {
          setShowEditCustomerModal(false);
          setSelectedCustomer(null);
        }}
        customer={selectedCustomer}
        onCustomerUpdated={handleCustomerUpdated}
      />

      {/* Change Password Modal */}
      <ChangePassword
        isOpen={showChangePasswordModal}
        onClose={() => {
          setShowChangePasswordModal(false);
          setSelectedCustomer(null);
        }}
        customer={selectedCustomer}
      />
    </div>
  );
};

export default AdminCustomers;